import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/cn';
import { buttonVariants } from './button.variants';
import { Tooltip } from './tooltip';

export interface ButtonProps extends React.ComponentProps<'button'>, VariantProps<typeof buttonVariants> {
  /** Render as the child element (e.g. a router <Link>) instead of a <button>. */
  asChild?: boolean;
  /** Disables the button and shows a spinner; the width does not change. */
  loading?: boolean;
  /**
   * Tooltip text. Icon-only buttons (`icon` / `iconSm` / `iconXs` / `iconLg`) default it to their
   * `aria-label`, so give them an `aria-label`. Pass `""` to opt out.
   */
  tooltip?: string;
}

export function Button({
  className,
  variant,
  size,
  asChild = false,
  loading = false,
  tooltip,
  type,
  disabled,
  children,
  ...props
}: ButtonProps) {
  const isIcon = size === 'icon' || size === 'iconSm' || size === 'iconXs' || size === 'iconLg';
  const tip = tooltip ?? (isIcon ? props['aria-label'] : undefined);
  const classes = cn(buttonVariants({ variant, size }), loading && 'relative', className);
  // Stable hooks for the redesigned kit (kit-next.css); they carry no styling by themselves.
  const kitAttrs = { 'data-slot': 'button', 'data-variant': variant ?? 'primary', 'data-size': size ?? 'md' };
  const isDisabled = (disabled ?? false) || loading;

  const button = asChild ? (
    <Slot className={classes} {...kitAttrs} aria-busy={loading || undefined} {...props}>
      {children}
    </Slot>
  ) : (
    <button
      type={type ?? 'button'}
      className={classes}
      {...kitAttrs}
      disabled={isDisabled}
      aria-busy={loading || undefined}
      {...props}
    >
      {loading ? (
        <>
          <span className="invisible inline-flex items-center justify-center gap-2">{children}</span>
          <span
            role="status"
            aria-label="Cargando"
            className="absolute size-4 animate-spin rounded-full border-2 border-current/30 border-t-current"
          />
        </>
      ) : (
        children
      )}
    </button>
  );

  if (!tip) return button;
  // A disabled <button> swallows pointer events, so its tooltip (which is
  // where "why is this disabled" lives) hangs off a wrapper instead.
  return <Tooltip content={tip}>{isDisabled && !asChild ? <span className="inline-flex">{button}</span> : button}</Tooltip>;
}
